import Phaser from 'phaser';
import type { Entity } from '@/types';
import type { SpeciesDef } from './species';
import type { RelationResolver } from '@/systems/RelationResolver';
import type { PerceptionSystem } from '@/systems/PerceptionSystem';
import type { ThreatAuraSystem } from '@/systems/ThreatAuraSystem';

const TEXTURE_PREFIX = 'vermin-';

export type VerminBehavior = 'wander' | 'chase' | 'flee';

export class VerminEntity extends Phaser.Physics.Arcade.Image {
  readonly entityId: string;
  readonly speciesId: string;
  mass: number;

  private dirX = 0;
  private dirY = 0;
  private nextWanderChangeMs = 0;
  private behavior: VerminBehavior = 'wander';

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    public readonly species: SpeciesDef,
    id: string,
  ) {
    VerminEntity.ensureTexture(scene, species);
    super(scene, x, y, TEXTURE_PREFIX + species.id);
    scene.add.existing(this);
    scene.physics.add.existing(this);
    this.entityId = id;
    this.speciesId = species.id;
    this.mass = species.mass;
    this.setCircle(species.bodyRadius);
    this.setCollideWorldBounds(true);
    this.randomizeDirection();
  }

  get entity(): Entity {
    return {
      id: this.entityId,
      speciesId: this.speciesId,
      mass: this.mass,
      isStatic: this.species.isStatic,
    };
  }

  update(
    dtMs: number,
    blobEntity: Entity,
    blobPos: Phaser.Math.Vector2,
    relations: RelationResolver,
    perception: PerceptionSystem,
    threatAura: ThreatAuraSystem,
  ): void {
    // Static food: no AI ever runs
    if (this.species.isStatic) {
      this.setVelocity(0, 0);
      return;
    }

    const selfEntity = this.entity;
    const pos = { x: this.x, y: this.y };

    // Unshakeable (alligator, humans) only react by vision, never flee aura
    const unshakeable = this.species.unshakeable === true;

    // Threat-aura flee: skittish creatures bolt when the ooze is near,
    // even without direct line of sight.
    if (!unshakeable && this.species.fleeSpeed > 0) {
      const extra = this.species.fearRange ?? 0;
      if (threatAura.isWithinAura(blobPos, pos, extra)) {
        this.behavior = 'flee';
        const dx = this.x - blobPos.x;
        const dy = this.y - blobPos.y;
        const d = Math.hypot(dx, dy);
        if (d > 0.1) {
          this.dirX = dx / d;
          this.dirY = dy / d;
        }
        this.setVelocity(
          this.dirX * this.species.fleeSpeed,
          this.dirY * this.species.fleeSpeed,
        );
        return;
      }
    }

    // Vision-based chase (larger predators that can see blob as prey)
    const blobIsPreyOfThis = relations.resolve(selfEntity, blobEntity) === 'PREY';
    const blobIsPredatorOfThis = relations.resolve(selfEntity, blobEntity) === 'PREDATOR';

    const hasVision = this.species.visionRange > 0;
    let canSeeBlob = false;
    if (hasVision) {
      const dirVec =
        Math.hypot(this.dirX, this.dirY) < 0.01
          ? { x: 1, y: 0 }
          : { x: this.dirX, y: this.dirY };
      canSeeBlob = perception.canSee(
        {
          origin: pos,
          direction: dirVec,
          range: this.species.visionRange,
          halfAngleRad: this.species.visionHalfAngleRad,
        },
        { x: blobPos.x, y: blobPos.y },
      );
    }

    if (canSeeBlob && blobIsPreyOfThis && this.species.chaseSpeed > 0) {
      this.behavior = 'chase';
      const dx = blobPos.x - this.x;
      const dy = blobPos.y - this.y;
      const d = Math.hypot(dx, dy);
      if (d > 0.1) {
        this.dirX = dx / d;
        this.dirY = dy / d;
      }
      this.setVelocity(this.dirX * this.species.chaseSpeed, this.dirY * this.species.chaseSpeed);
      return;
    }

    if (canSeeBlob && blobIsPredatorOfThis && !unshakeable && this.species.fleeSpeed > 0) {
      this.behavior = 'flee';
      const dx = this.x - blobPos.x;
      const dy = this.y - blobPos.y;
      const d = Math.hypot(dx, dy);
      if (d > 0.1) {
        this.dirX = dx / d;
        this.dirY = dy / d;
      }
      this.setVelocity(this.dirX * this.species.fleeSpeed, this.dirY * this.species.fleeSpeed);
      return;
    }

    // Wander
    this.behavior = 'wander';
    this.nextWanderChangeMs -= dtMs;
    if (this.nextWanderChangeMs <= 0) {
      this.randomizeDirection();
      this.nextWanderChangeMs = 800 + Math.random() * 1500;
    }
    this.setVelocity(
      this.dirX * this.species.wanderSpeed,
      this.dirY * this.species.wanderSpeed,
    );
  }

  private randomizeDirection(): void {
    const angle = Math.random() * Math.PI * 2;
    this.dirX = Math.cos(angle);
    this.dirY = Math.sin(angle);
  }

  private static ensureTexture(scene: Phaser.Scene, species: SpeciesDef): void {
    const key = TEXTURE_PREFIX + species.id;
    if (scene.textures.exists(key)) return;
    const size = species.bodyRadius * 2 + 4;
    const g = scene.add.graphics();
    g.fillStyle(species.color, 1);
    g.fillCircle(size / 2, size / 2, species.bodyRadius);
    g.lineStyle(1, 0x000000, 0.6);
    g.strokeCircle(size / 2, size / 2, species.bodyRadius);
    g.generateTexture(key, size, size);
    g.destroy();
  }
}
